// src/features/business/pages/BusinessDetailPage.tsx
// PREMIUM REDESIGN: EXECUTIVE DASHBOARD, HORIZONTAL TABS, BRANCH-SPECIFIC STAFF ROSTER

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import * as businessApi from '../services/businessApi';
import * as bankingApi from '../../banking/services/bankingApi';
import * as settingsApi from '../../settings/services/settingsApi';
import type { BankAccount } from '../../banking/services/bankingApi';
import type { Business } from '../services/businessApi';
import {
    Building2, ArrowLeft, Store, CreditCard, Users as UsersIcon,
    MapPin, Phone, Mail, Globe, Pencil, Plus, Trash2, Save,
    CheckCircle2, DollarSign, Wallet, PiggyBank, Landmark,
    X, Star, Activity, Coins, Calendar, Search, UserPlus,
    Sparkles, Shield, Lock, Copy, Check, ChevronRight, ArrowUpRight,
    Layers, UserCheck, Link2, Unlink
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES & CONFIGS
// ============================================================

interface Branch {
    id: string;
    name: string;
    code: string;
    businessId?: string;
    address: string | null;
    phone: string | null;
    email: string | null;
    isMainBranch: boolean;
    isActive: boolean;
    businessName?: string;
}

interface StaffMember {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    roles: { id: string; name: string }[];
    totalSales?: string;
}

interface Currency {
    id: string;
    code: string;
    name: string;
    symbol: string;
    isBaseCurrency: boolean;
    exchangeRate: number;
    decimalPlaces?: number;
}

type TabType = 'overview' | 'branches' | 'staff' | 'accounts' | 'currencies';

const businessTypes: Record<string, { label: string; emoji: string; gradient: string }> = {
    retail: { label: 'Retail Store', emoji: '🏪', gradient: 'from-blue-500 to-indigo-600' },
    restaurant: { label: 'Restaurant & Bar', emoji: '🍽️', gradient: 'from-amber-500 to-orange-600' },
    wholesale: { label: 'Wholesale', emoji: '📦', gradient: 'from-purple-500 to-violet-600' },
    pharmacy: { label: 'Pharmacy', emoji: '💊', gradient: 'from-emerald-500 to-teal-600' },
    service: { label: 'Service Business', emoji: '🔧', gradient: 'from-rose-500 to-pink-600' },
};

const accountTypeConfig: Record<string, { label: string; icon: React.ElementType; gradient: string; bgLight: string; border: string }> = {
    savings: { label: 'Savings', icon: PiggyBank, gradient: 'from-emerald-500 to-teal-600', bgLight: 'bg-emerald-50', border: 'border-emerald-200' },
    current: { label: 'Current', icon: Landmark, gradient: 'from-blue-500 to-indigo-600', bgLight: 'bg-blue-50', border: 'border-blue-200' },
    cash: { label: 'Cash Vault', icon: Wallet, gradient: 'from-amber-500 to-orange-600', bgLight: 'bg-amber-50', border: 'border-amber-200' },
    other: { label: 'Other', icon: CreditCard, gradient: 'from-purple-500 to-violet-600', bgLight: 'bg-purple-50', border: 'border-purple-200' },
};

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0.00';
    if (num >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

const formatDate = (date: string) => {
    try {
        return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
        return 'N/A';
    }
};

// ============================================================
// MODAL
// ============================================================

function Modal({ open, onClose, title, icon: Icon, children }: {
    open: boolean; onClose: () => void; title: string; icon: React.ElementType; children: React.ReactNode;
}) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    onClick={onClose}>
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 10 }}
                        onClick={e => e.stopPropagation()}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-slate-100">
                        <div className="sticky top-0 bg-white rounded-t-3xl z-10">
                            <div className="px-7 py-5 bg-gradient-to-r from-teal-700 via-emerald-600 to-teal-800 rounded-t-3xl text-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white/20 backdrop-blur-sm shadow-inner">
                                            <Icon size={20} className="text-white" />
                                        </div>
                                        <h2 className="text-lg font-bold text-white tracking-tight">{title}</h2>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="p-2 rounded-xl bg-white/15 text-white hover:bg-white/30 transition-colors">
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="p-7">{children}</div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BusinessDetailPage() {
    const { businessId } = useParams<{ businessId: string }>();
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);
    const tenant = useAuthStore(s => s.tenant);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    // Active Tab
    const [activeTab, setActiveTab] = useState<TabType>('overview');

    // Modals
    const [showEditBusiness, setShowEditBusiness] = useState(false);
    const [showBranchForm, setShowBranchForm] = useState(false);
    const [showAccountForm, setShowAccountForm] = useState(false);
    const [showAssignAccountModal, setShowAssignAccountModal] = useState(false);
    const [assignAccountModalSearch, setAssignAccountModalSearch] = useState('');
    const [showStaffAssignModal, setShowStaffAssignModal] = useState(false);
    const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
    const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{ type: string; id: string; name: string } | null>(null);
    const [copiedCode, setCopiedCode] = useState(false);

    // Filter by branch inside Staff Tab
    const [selectedBranchFilter, setSelectedBranchFilter] = useState<string>('all');
    const [staffSearchTerm, setStaffSearchTerm] = useState('');

    // Assign modal state
    const [assignTargetBranchId, setAssignTargetBranchId] = useState<string>('');
    const [modalSelectedStaffIds, setModalSelectedStaffIds] = useState<string[]>([]);
    const [assignModalSearch, setAssignModalSearch] = useState('');

    // Form errors
    const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});
    const [branchFormErrors, setBranchFormErrors] = useState<Record<string, string>>({});

    // Forms
    const [editForm, setEditForm] = useState({
        name: '', shortCode: '', type: 'retail', description: '', brandColor: '#0D9488',
        contactEmail: '', contactPhone: '', website: '', address: ''
    });

    const [branchForm, setBranchForm] = useState({
        name: '', code: '', address: '', phone: '', email: '', isMainBranch: false
    });

    const [accountForm, setAccountForm] = useState({
        bankName: '', accountNumber: '', accountName: '', accountType: 'current',
        currency: 'NGN', openingBalance: '0', branchId: '', isDefault: false
    });

    // ==================== ERROR PARSING ====================

    const extractErrorMessage = (err: any): string => {
        const errorData = err?.response?.data?.error;
        if (!errorData) return err?.message || 'An unexpected error occurred.';
        if (errorData.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
            return errorData.details.map((d: any) => d.message).join('. ');
        }
        if (typeof errorData === 'string') return errorData;
        if (errorData.message && typeof errorData.message === 'string') return errorData.message;
        return err?.message || 'An unexpected error occurred.';
    };

    const extractFieldErrors = (err: any): Record<string, string> => {
        const errorData = err?.response?.data?.error;
        if (!errorData?.details || !Array.isArray(errorData.details)) return {};
        const fieldErrors: Record<string, string> = {};
        errorData.details.forEach((detail: { field: string; message: string }) => {
            if (detail.field) fieldErrors[detail.field] = detail.message;
        });
        return fieldErrors;
    };

    // ==================== QUERIES ====================

    const { data: business, isLoading: isLoadingBusiness, isError } = useQuery<Business>({
        queryKey: ['business', businessId],
        queryFn: () => businessApi.getBusinessById(businessId!),
        enabled: !!businessId,
    });

    const { data: branches } = useQuery<Branch[]>({
        queryKey: ['branches', tenantId],
        queryFn: async () => {
            const response = await settingsApi.getBranches(tenantId!);
            return Array.isArray(response) ? response : (response as any)?.data || [];
        },
        enabled: !!tenantId,
    });

    // Branches belonging to this business
    const businessBranches = useMemo(() => {
        return (branches || []).filter((b: any) => b.businessId === businessId);
    }, [branches, businessId]);

    // Query branch staff for each branch of this business
    const { data: branchStaffMap, isLoading: isLoadingBranchStaff } = useQuery<Record<string, StaffMember[]>>({
        queryKey: ['business-branch-staff', businessId, businessBranches.map(b => b.id).join(',')],
        queryFn: async () => {
            if (!tenantId || businessBranches.length === 0) return {};
            const map: Record<string, StaffMember[]> = {};
            await Promise.all(
                businessBranches.map(async (branch) => {
                    try {
                        const res = await settingsApi.getBranchStaff(tenantId, branch.id);
                        const list = Array.isArray(res) ? res : (res as any)?.data || [];
                        map[branch.id] = list;
                    } catch {
                        map[branch.id] = [];
                    }
                })
            );
            return map;
        },
        enabled: !!tenantId && businessBranches.length > 0,
    });

    // All organization staff (only for the assign-to-branch selection modal)
    const { data: allTenantStaff, isLoading: isLoadingAllStaff } = useQuery<StaffMember[]>({
        queryKey: ['staff', tenantId],
        queryFn: async () => {
            const response = await settingsApi.getStaff(tenantId!);
            if (Array.isArray(response)) return response;
            if ((response as any)?.data && Array.isArray((response as any).data)) return (response as any).data;
            if ((response as any)?.users && Array.isArray((response as any).users)) return (response as any).users;
            return [];
        },
        enabled: !!tenantId,
    });

    const { data: accounts } = useQuery<BankAccount[]>({
        queryKey: ['bank-accounts', tenantId, businessId],
        queryFn: () => bankingApi.getBankAccounts({ businessId }),
        enabled: !!tenantId && !!businessId,
    });

    const { data: allTenantAccounts = [] } = useQuery<BankAccount[]>({
        queryKey: ['all-bank-accounts', tenantId],
        queryFn: () => bankingApi.getBankAccounts(),
        enabled: !!tenantId,
    });

    const { data: currencies } = useQuery<Currency[]>({
        queryKey: ['currencies', tenantId],
        queryFn: async () => {
            try {
                const { data } = await settingsApi.getCurrencies(tenantId!);
                return data?.data || [];
            } catch {
                return [];
            }
        },
        enabled: !!tenantId,
    });

    // ==================== DERIVED DATA & STAFF AGGREGATION ====================

    const isSingleType = tenant?.businessModel === 'single' || business?.type?.toLowerCase() === 'single';
    const canAddBranch = !isSingleType || businessBranches.length === 0;

    const activeBranches = businessBranches.filter(b => b.isActive);
    const businessAccounts = accounts || [];
    const totalBalance = businessAccounts.reduce((s, a) => s + parseFloat(a.currentBalance || '0'), 0);

    // Aggregate staff working across branches of THIS business
    const branchStaffRoster = useMemo(() => {
        if (!branchStaffMap) return [];
        const map = new Map<string, { staff: StaffMember; branches: Branch[] }>();

        businessBranches.forEach(branch => {
            const members = branchStaffMap[branch.id] || [];
            members.forEach(member => {
                const existing = map.get(member.id);
                if (existing) {
                    if (!existing.branches.some(b => b.id === branch.id)) {
                        existing.branches.push(branch);
                    }
                } else {
                    map.set(member.id, { staff: member, branches: [branch] });
                }
            });
        });

        return Array.from(map.values());
    }, [branchStaffMap, businessBranches]);

    // Filter staff roster by selected branch and search query
    const filteredRoster = useMemo(() => {
        return branchStaffRoster.filter(entry => {
            if (selectedBranchFilter !== 'all') {
                if (!entry.branches.some(b => b.id === selectedBranchFilter)) return false;
            }
            if (staffSearchTerm) {
                const q = staffSearchTerm.toLowerCase();
                const name = `${entry.staff.firstName} ${entry.staff.lastName}`.toLowerCase();
                const email = entry.staff.email.toLowerCase();
                const role = entry.staff.roles?.some(r => r.name.toLowerCase().includes(q));
                return name.includes(q) || email.includes(q) || role;
            }
            return true;
        });
    }, [branchStaffRoster, selectedBranchFilter, staffSearchTerm]);

    const totalStaffCount = branchStaffRoster.length;
    const activeStaffCount = branchStaffRoster.filter(r => r.staff.isActive).length;

    // ==================== MUTATIONS ====================

    const updateBusinessMutation = useMutation({
        mutationFn: (payload: Record<string, unknown>) => {
            const cleanPayload = Object.fromEntries(
                Object.entries(payload).filter(([_, v]) => v !== undefined && v !== null && v !== '')
            );
            return businessApi.updateBusiness(businessId!, cleanPayload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['business', businessId] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            addToast({ type: 'success', title: 'Business Updated ✨', message: 'Your business profile changes were saved.' });
            setShowEditBusiness(false);
            setEditFormErrors({});
        },
        onError: (err: any) => {
            const fieldErrors = extractFieldErrors(err);
            if (Object.keys(fieldErrors).length > 0) setEditFormErrors(fieldErrors);
            addToast({ type: 'error', title: 'Update Failed', message: extractErrorMessage(err) });
        },
    });

    const saveBranchMutation = useMutation({
        mutationFn: async () => {
            if (!editingBranch && !canAddBranch) {
                throw new Error('Single-type businesses are restricted to 1 primary branch. Only Multi-Business accounts can create additional branches.');
            }
            const payload = {
                name: branchForm.name.trim(),
                code: branchForm.code.trim().toUpperCase(),
                address: branchForm.address.trim() || undefined,
                phone: branchForm.phone.trim() || undefined,
                email: branchForm.email.trim() || undefined,
                isMainBranch: branchForm.isMainBranch,
                businessId: businessId!
            };
            if (editingBranch) return settingsApi.updateBranch(tenantId!, editingBranch.id, payload);
            return settingsApi.createBranch(tenantId!, payload as any);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['branches'] });
            queryClient.invalidateQueries({ queryKey: ['business', businessId] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['businesses-stats'] });
            addToast({
                type: 'success',
                title: editingBranch ? 'Branch Updated ✨' : 'Branch Created 🏪',
                message: `${branchForm.name} was successfully ${editingBranch ? 'updated' : 'registered'}.`
            });
            setShowBranchForm(false);
            setEditingBranch(null);
            setBranchFormErrors({});
        },
        onError: (err: any) => {
            const fe = extractFieldErrors(err);
            if (Object.keys(fe).length > 0) setBranchFormErrors(fe);
            addToast({ type: 'error', title: 'Failed to Save Branch', message: extractErrorMessage(err) });
        },
    });

    const saveAccountMutation = useMutation({
        mutationFn: async () => {
            const payload: any = {
                ...accountForm,
                businessId,
                openingBalance: parseFloat(accountForm.openingBalance) || 0,
                branchId: accountForm.branchId || undefined,
            };
            if (editingAccount) return bankingApi.updateBankAccount(editingAccount.id, payload);
            return bankingApi.createBankAccount(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['all-bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['business', businessId] });
            addToast({ type: 'success', title: 'Bank Account Saved', message: 'Account details have been recorded.' });
            setShowAccountForm(false);
            setEditingAccount(null);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed to Save Account', message: extractErrorMessage(err) }),
    });

    const assignAccountMutation = useMutation({
        mutationFn: async ({ accountId, targetBusinessId, targetBranchId }: { accountId: string; targetBusinessId: string | null; targetBranchId?: string | null }) => {
            return bankingApi.updateBankAccount(accountId, {
                businessId: targetBusinessId,
                branchId: targetBranchId !== undefined ? targetBranchId : (targetBusinessId ? undefined : null),
            });
        },
        onSuccess: (_, vars) => {
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['all-bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['business', businessId] });
            addToast({
                type: 'success',
                title: vars.targetBusinessId ? 'Account Assigned 🏦' : 'Account Unlinked',
                message: vars.targetBusinessId
                    ? `Bank account assigned to ${business?.name}.`
                    : `Bank account unlinked from ${business?.name}.`,
            });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Action Failed', message: extractErrorMessage(err) }),
    });

    const filteredAssignAccounts = useMemo(() => {
        if (!assignAccountModalSearch.trim()) return allTenantAccounts;
        const q = assignAccountModalSearch.toLowerCase();
        return allTenantAccounts.filter(a =>
            a.bankName.toLowerCase().includes(q) ||
            a.accountNumber.toLowerCase().includes(q) ||
            a.accountName.toLowerCase().includes(q)
        );
    }, [allTenantAccounts, assignAccountModalSearch]);

    const assignStaffMutation = useMutation({
        mutationFn: async () => {
            if (!assignTargetBranchId) throw new Error('Please select a branch to assign staff to.');
            return settingsApi.assignStaffToBranch(tenantId!, assignTargetBranchId, modalSelectedStaffIds);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['business-branch-staff'] });
            queryClient.invalidateQueries({ queryKey: ['branch-staff'] });
            queryClient.invalidateQueries({ queryKey: ['branches'] });
            queryClient.invalidateQueries({ queryKey: ['business', businessId] });
            const branchName = businessBranches.find(b => b.id === assignTargetBranchId)?.name || 'branch';
            addToast({
                type: 'success',
                title: 'Branch Staff Assigned',
                message: `${modalSelectedStaffIds.length} staff member(s) assigned to ${branchName}.`
            });
            setShowStaffAssignModal(false);
            setModalSelectedStaffIds([]);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Assignment Failed', message: extractErrorMessage(err) }),
    });

    const deleteMutation = useMutation({
        mutationFn: ({ type, id }: { type: string; id: string }) => {
            if (type === 'account') return bankingApi.deleteBankAccount(id);
            return Promise.resolve();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['business', businessId] });
            addToast({ type: 'success', title: 'Deleted', message: `${deleteTarget?.name} has been removed.` });
            setDeleteTarget(null);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Delete Failed', message: extractErrorMessage(err) }),
    });

    // ==================== HANDLERS ====================

    const handleCopyCode = () => {
        if (!business?.shortCode) return;
        navigator.clipboard.writeText(business.shortCode);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
        addToast({ type: 'info', title: 'Copied', message: `Code ${business.shortCode} copied to clipboard.` });
    };

    const handleOpenEditBusiness = () => {
        if (!business) return;
        setEditForm({
            name: business.name,
            shortCode: business.shortCode,
            type: business.type || 'retail',
            description: business.description || '',
            brandColor: business.brandColor || '#0D9488',
            contactEmail: business.contactEmail || '',
            contactPhone: business.contactPhone || '',
            website: business.website || '',
            address: business.address || ''
        });
        setEditFormErrors({});
        setShowEditBusiness(true);
    };

    const handleOpenAssignModalForBranch = (branchIdToAssign: string) => {
        setAssignTargetBranchId(branchIdToAssign);
        // Pre-select staff currently in that branch
        const current = (branchStaffMap?.[branchIdToAssign] || []).map(s => s.id);
        setModalSelectedStaffIds(current);
        setAssignModalSearch('');
        setShowStaffAssignModal(true);
    };

    const toggleModalStaffSelection = (staffId: string) => {
        setModalSelectedStaffIds(prev =>
            prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]
        );
    };

    // ==================== LOADING & ERROR ====================

    if (isLoadingBusiness) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center">
                <Spinner size="xl" variant="teal" />
            </div>
        );
    }

    if (isError || !business) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <ErrorState title="Business not found" onBack={() => navigate('/businesses')} />
            </div>
        );
    }

    const typeConfig = businessTypes[business.type] || businessTypes.retail;
    const brandColor = business.brandColor || '#0D9488';

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-6">

                {/* Back to Businesses */}
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
                    <button
                        onClick={() => navigate('/businesses')}
                        className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-teal-700 group transition-colors">
                        <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                        Back to Businesses
                    </button>
                </motion.div>

                {/* ========== EXECUTIVE HERO BANNER ========== */}
                <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden rounded-3xl shadow-2xl text-white"
                    style={{
                        background: `linear-gradient(135deg, ${brandColor} 0%, #0d9488 55%, #065f46 100%)`
                    }}>

                    {/* Ambient Glows */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.25),transparent_50%)]" />
                        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-black/15 rounded-full blur-3xl" />
                    </div>

                    <div className="relative px-6 lg:px-10 py-8 lg:py-10">
                        {/* Top row */}
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">

                            {/* Left: Avatar & Identity */}
                            <div className="flex items-start sm:items-center gap-5">
                                <motion.div
                                    whileHover={{ scale: 1.05, rotate: -2 }}
                                    className="w-20 h-20 lg:w-24 lg:h-24 rounded-3xl flex items-center justify-center text-white text-3xl lg:text-4xl font-black bg-white/15 backdrop-blur-md ring-1 ring-white/30 shadow-2xl flex-shrink-0">
                                    {business.name.charAt(0).toUpperCase()}
                                </motion.div>

                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2.5 flex-wrap">
                                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight">
                                            {business.name}
                                        </h1>
                                        <Badge className={clsx(
                                            'backdrop-blur-sm font-semibold border',
                                            business.isActive
                                                ? 'bg-emerald-400/25 text-emerald-100 border-emerald-300/40'
                                                : 'bg-rose-500/25 text-rose-200 border-rose-300/40'
                                        )}>
                                            {business.isActive ? 'Active' : 'Inactive'}
                                        </Badge>

                                        {/* Business Model Pill */}
                                        {isSingleType ? (
                                            <Badge className="bg-amber-400/20 text-amber-200 border-amber-300/30 font-medium">
                                                <Lock size={10} className="mr-1 inline" /> Single-Business Type
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-white/15 text-white/90 border-white/20 font-medium">
                                                <Layers size={10} className="mr-1 inline" /> Multi-Branch Enterprise
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Type & Shortcode */}
                                    <div className="flex items-center gap-2 flex-wrap text-emerald-100 text-sm font-medium">
                                        <span>{typeConfig.emoji} {typeConfig.label}</span>
                                        <span className="text-white/40">•</span>
                                        <button
                                            onClick={handleCopyCode}
                                            title="Click to copy business code"
                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-mono transition-colors">
                                            <span>{business.shortCode}</span>
                                            {copiedCode ? <Check size={11} className="text-emerald-300" /> : <Copy size={11} className="text-white/70" />}
                                        </button>
                                    </div>

                                    {/* Contact channels */}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-white/70 text-xs">
                                        {business.address && (
                                            <span className="flex items-center gap-1"><MapPin size={11} className="text-teal-200" /> {business.address}</span>
                                        )}
                                        {business.contactPhone && (
                                            <span className="flex items-center gap-1"><Phone size={11} className="text-teal-200" /> {business.contactPhone}</span>
                                        )}
                                        {business.contactEmail && (
                                            <span className="flex items-center gap-1"><Mail size={11} className="text-teal-200" /> {business.contactEmail}</span>
                                        )}
                                        {business.website && (
                                            <span className="flex items-center gap-1"><Globe size={11} className="text-teal-200" /> {business.website}</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Balance & Action Buttons */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto justify-between lg:justify-end border-t lg:border-t-0 border-white/15 pt-4 lg:pt-0">
                                <div className="text-left lg:text-right">
                                    <p className="text-white/60 text-[10px] uppercase tracking-widest font-bold">Total Vault Balance</p>
                                    <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight">
                                        {formatNaira(totalBalance)}
                                    </p>
                                    <p className="text-teal-100 text-xs">{businessAccounts.length} bank accounts linked</p>
                                </div>

                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        leftIcon={<Pencil size={15} />}
                                        onClick={handleOpenEditBusiness}
                                        className="!bg-white/20 !border-white/30 !text-white hover:!bg-white/30 !backdrop-blur-md !font-bold">
                                        Edit
                                    </Button>

                                    {canAddBranch ? (
                                        <Button
                                            size="md"
                                            leftIcon={<Plus size={15} />}
                                            onClick={() => {
                                                setEditingBranch(null);
                                                setBranchForm({ name: '', code: '', address: '', phone: '', email: '', isMainBranch: false });
                                                setBranchFormErrors({});
                                                setShowBranchForm(true);
                                            }}
                                            className="!bg-white !text-teal-800 hover:!bg-teal-50 !font-black !shadow-lg shadow-black/20">
                                            + Add Branch
                                        </Button>
                                    ) : (
                                        <div
                                            title="Single business model: limited to 1 primary branch"
                                            className="px-3.5 py-2.5 rounded-xl bg-black/20 border border-white/15 text-white/70 text-xs font-semibold flex items-center gap-1.5 cursor-not-allowed">
                                            <Lock size={13} className="text-amber-300" />
                                            <span>Single Branch Limit</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Bottom metrics ribbon */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-8 pt-6 border-t border-white/15">
                            {[
                                { label: 'Branches', value: businessBranches.length, icon: Store },
                                { label: 'Active Branches', value: activeBranches.length, icon: CheckCircle2 },
                                { label: 'Deployed Staff', value: totalStaffCount, icon: UsersIcon },
                                { label: 'Bank Accounts', value: businessAccounts.length, icon: CreditCard },
                                { label: 'Currencies', value: currencies?.length || 0, icon: Coins },
                                { label: 'Created', value: formatDate(business.createdAt), icon: Calendar },
                            ].map(item => (
                                <div key={item.label} className="bg-white/10 rounded-2xl p-3 backdrop-blur-sm border border-white/10">
                                    <div className="flex items-center gap-1.5 text-teal-200 text-[10px] uppercase font-bold tracking-wider mb-1">
                                        <item.icon size={12} />
                                        <span>{item.label}</span>
                                    </div>
                                    <p className="text-base font-extrabold text-white truncate">{item.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Single Business Notice */}
                {isSingleType && businessBranches.length >= 1 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-900 flex items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-amber-100 text-amber-700 flex-shrink-0">
                                <Lock size={18} />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-amber-950">
                                    Single-Business Model: 1 Dedicated Location
                                </p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    This business is marked as a Single Business type and already has its primary operating branch. Only Multi-Business accounts can create additional branches.
                                </p>
                            </div>
                        </div>
                        <Badge variant="warning" size="sm" className="flex-shrink-0">
                            1 / 1 Branch
                        </Badge>
                    </motion.div>
                )}

                {/* ========== MODERN HORIZONTAL TAB BAR ========== */}
                <div className="flex items-center justify-between border-b border-slate-200 pb-1">
                    <div className="flex items-center gap-2 overflow-x-auto py-1">
                        {[
                            { key: 'overview' as const, label: 'Overview', icon: Activity, count: null },
                            { key: 'branches' as const, label: 'Branches', icon: Store, count: businessBranches.length },
                            { key: 'staff' as const, label: 'Branch Staff', icon: UsersIcon, count: totalStaffCount },
                            { key: 'accounts' as const, label: 'Bank Accounts', icon: CreditCard, count: businessAccounts.length },
                            { key: 'currencies' as const, label: 'Currencies', icon: Coins, count: currencies?.length || 0 },
                        ].map(tab => {
                            const isActive = activeTab === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={clsx(
                                        'relative flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap',
                                        isActive
                                            ? 'text-teal-700 bg-teal-50 border border-teal-200/80 shadow-xs'
                                            : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100/70'
                                    )}>
                                    <tab.icon size={16} className={isActive ? 'text-teal-600' : 'text-slate-400'} />
                                    <span>{tab.label}</span>
                                    {tab.count !== null && (
                                        <span className={clsx(
                                            'px-2 py-0.5 rounded-full text-xs font-semibold',
                                            isActive
                                                ? 'bg-teal-600 text-white'
                                                : 'bg-slate-200 text-slate-600'
                                        )}>
                                            {tab.count}
                                        </span>
                                    )}
                                    {isActive && (
                                        <motion.div
                                            layoutId="businessActiveTabIndicator"
                                            className="absolute bottom-0 left-2 right-2 h-0.5 bg-teal-600 rounded-full"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Tab-specific action buttons */}
                    {activeTab === 'branches' && canAddBranch && (
                        <Button
                            size="sm"
                            leftIcon={<Plus size={14} />}
                            onClick={() => {
                                setEditingBranch(null);
                                setBranchForm({ name: '', code: '', address: '', phone: '', email: '', isMainBranch: false });
                                setBranchFormErrors({});
                                setShowBranchForm(true);
                            }}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            Add Branch
                        </Button>
                    )}

                    {activeTab === 'staff' && businessBranches.length > 0 && (
                        <Button
                            size="sm"
                            leftIcon={<UserPlus size={14} />}
                            onClick={() => handleOpenAssignModalForBranch(businessBranches[0].id)}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            Assign Staff to Branch
                        </Button>
                    )}

                    {activeTab === 'accounts' && (
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<Link2 size={14} />}
                                onClick={() => {
                                    setAssignAccountModalSearch('');
                                    setShowAssignAccountModal(true);
                                }}
                                className="border-teal-200 text-teal-800 hover:bg-teal-50 font-semibold">
                                Assign Existing Account
                            </Button>
                            <Button
                                size="sm"
                                leftIcon={<Plus size={14} />}
                                onClick={() => {
                                    setEditingAccount(null);
                                    setAccountForm({
                                        bankName: '', accountNumber: '', accountName: '', accountType: 'current',
                                        currency: 'NGN', openingBalance: '0', branchId: '', isDefault: false
                                    });
                                    setShowAccountForm(true);
                                }}
                                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                Add Account
                            </Button>
                        </div>
                    )}
                </div>

                {/* ========== TAB CONTENT ========== */}
                <AnimatePresence mode="wait">

                    {/* ---------- OVERVIEW TAB ---------- */}
                    {activeTab === 'overview' && (
                        <motion.div
                            key="overview-tab"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-6">

                            {/* 3 Metric Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Branch Network</p>
                                        <p className="text-3xl font-black text-slate-900 mt-1">
                                            {activeBranches.length} <span className="text-sm font-semibold text-slate-400">/ {businessBranches.length} active</span>
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">Operational branch locations</p>
                                    </div>
                                    <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600">
                                        <Store size={26} />
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Branch Staff</p>
                                        <p className="text-3xl font-black text-purple-600 mt-1">
                                            {totalStaffCount} <span className="text-sm font-semibold text-slate-400">assigned</span>
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">{activeStaffCount} active staff members</p>
                                    </div>
                                    <div className="p-3.5 rounded-2xl bg-purple-50 text-purple-600">
                                        <UsersIcon size={26} />
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Vault Balance</p>
                                        <p className="text-3xl font-black text-emerald-600 mt-1">
                                            {formatNaira(totalBalance)}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">{businessAccounts.length} accounts configured</p>
                                    </div>
                                    <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600">
                                        <Wallet size={26} />
                                    </div>
                                </div>
                            </div>

                            {/* Business Profile Details & Quick Actions */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 shadow-sm p-6 lg:p-8 space-y-6">
                                    <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                                        <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                                            <Building2 size={18} className="text-teal-600" />
                                            Business Entity Profile
                                        </h3>
                                        <Button size="sm" variant="secondary" onClick={handleOpenEditBusiness} leftIcon={<Pencil size={13} />}>
                                            Edit Profile
                                        </Button>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Business Name</p>
                                            <p className="text-base font-bold text-slate-900">{business.name}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Short Code</p>
                                            <code className="text-sm font-mono font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-200">
                                                {business.shortCode}
                                            </code>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Industry / Sector</p>
                                            <p className="text-sm font-medium text-slate-800">{typeConfig.emoji} {typeConfig.label}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Business Model</p>
                                            <p className="text-sm font-medium text-slate-800">
                                                {isSingleType ? 'Single-Location Entity' : 'Multi-Branch Entity'}
                                            </p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Physical Address</p>
                                            <p className="text-sm font-medium text-slate-700">{business.address || 'No address provided'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Contact Channels</p>
                                            <p className="text-sm font-medium text-slate-700">
                                                {business.contactPhone || 'No phone'} · {business.contactEmail || 'No email'}
                                            </p>
                                        </div>
                                    </div>

                                    {business.description && (
                                        <div className="pt-4 border-t border-slate-100">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">About this Business</p>
                                            <p className="text-sm text-slate-600 leading-relaxed">{business.description}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Right Side: Shortcuts */}
                                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 lg:p-8 space-y-4">
                                    <h3 className="font-bold text-slate-900 text-lg">Quick Actions</h3>

                                    <div className="space-y-2.5">
                                        <button
                                            onClick={() => setActiveTab('branches')}
                                            className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left group">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-teal-50 text-teal-600 group-hover:bg-teal-100">
                                                    <Store size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-900">Manage Branches</p>
                                                    <p className="text-xs text-slate-400">{businessBranches.length} locations</p>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-slate-300 group-hover:text-teal-600 transition-colors" />
                                        </button>

                                        <button
                                            onClick={() => setActiveTab('staff')}
                                            className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left group">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-purple-50 text-purple-600 group-hover:bg-purple-100">
                                                    <UsersIcon size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-900">Branch Staff Roster</p>
                                                    <p className="text-xs text-slate-400">{totalStaffCount} staff deployed</p>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-slate-300 group-hover:text-teal-600 transition-colors" />
                                        </button>

                                        <button
                                            onClick={() => setActiveTab('accounts')}
                                            className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left group">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-100">
                                                    <CreditCard size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-900">Bank Accounts</p>
                                                    <p className="text-xs text-slate-400">{businessAccounts.length} accounts</p>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-slate-300 group-hover:text-teal-600 transition-colors" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* ---------- BRANCHES TAB ---------- */}
                    {activeTab === 'branches' && (
                        <motion.div
                            key="branches-tab"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-4">

                            {businessBranches.length === 0 ? (
                                <EmptyState
                                    icon={<Store size={44} className="text-teal-600" />}
                                    title="No branches created yet"
                                    description="Create your first branch location for this business to start assigning staff and processing sales."
                                    action={canAddBranch ? {
                                        label: 'Add First Branch',
                                        onClick: () => {
                                            setEditingBranch(null);
                                            setBranchForm({ name: '', code: '', address: '', phone: '', email: '', isMainBranch: true });
                                            setBranchFormErrors({});
                                            setShowBranchForm(true);
                                        }
                                    } : undefined}
                                />
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {businessBranches.map(branch => {
                                        const branchAccounts = businessAccounts.filter(a => a.branchId === branch.id);
                                        const branchBalance = branchAccounts.reduce((s, a) => s + parseFloat(a.currentBalance || '0'), 0);
                                        const staffCountInBranch = (branchStaffMap?.[branch.id] || []).length;

                                        return (
                                            <motion.div
                                                key={branch.id}
                                                whileHover={{ y: -3 }}
                                                onClick={() => navigate(`/branches/${branch.id}`)}
                                                className="bg-white rounded-3xl border-2 border-slate-200 hover:border-teal-400 p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col justify-between">
                                                <div>
                                                    <div className="flex items-start justify-between gap-3 mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className={clsx(
                                                                'w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 shadow-xs',
                                                                branch.isMainBranch ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : 'bg-teal-100 text-teal-800'
                                                            )}>
                                                                <Store size={22} />
                                                            </div>
                                                            <div>
                                                                <h4 className="font-extrabold text-slate-900 text-base group-hover:text-teal-700 transition-colors">
                                                                    {branch.name}
                                                                </h4>
                                                                <code className="text-xs font-mono font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200 mt-1 inline-block">
                                                                    {branch.code}
                                                                </code>
                                                            </div>
                                                        </div>

                                                        <div className="flex flex-col items-end gap-1">
                                                            {branch.isMainBranch && <Badge variant="warning" size="sm">Main</Badge>}
                                                            <Badge variant={branch.isActive ? 'success' : 'danger'} size="sm">
                                                                {branch.isActive ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-1 text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
                                                        {branch.address && (
                                                            <p className="flex items-center gap-1.5 truncate">
                                                                <MapPin size={12} className="text-teal-600 flex-shrink-0" />
                                                                <span className="truncate">{branch.address}</span>
                                                            </p>
                                                        )}
                                                        {branch.phone && (
                                                            <p className="flex items-center gap-1.5">
                                                                <Phone size={12} className="text-teal-600 flex-shrink-0" />
                                                                <span>{branch.phone}</span>
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Vault & Staff mini badges */}
                                                    <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-slate-100 text-xs">
                                                        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                                                            <p className="text-[10px] uppercase font-bold text-slate-400">Staff</p>
                                                            <p className="font-bold text-slate-800">{staffCountInBranch} assigned</p>
                                                        </div>
                                                        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                                                            <p className="text-[10px] uppercase font-bold text-slate-400">Vault Balance</p>
                                                            <p className="font-bold text-emerald-600 truncate">{formatNaira(branchBalance)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                                                    <span className="text-xs font-semibold text-teal-700 flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                                                        Open Branch View <ArrowUpRight size={13} />
                                                    </span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingBranch(branch);
                                                            setBranchForm({
                                                                name: branch.name, code: branch.code,
                                                                address: branch.address || '', phone: branch.phone || '',
                                                                email: branch.email || '', isMainBranch: branch.isMainBranch
                                                            });
                                                            setBranchFormErrors({});
                                                            setShowBranchForm(true);
                                                        }}
                                                        className="text-xs font-semibold text-slate-500 hover:text-teal-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors">
                                                        Edit
                                                    </button>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* ---------- BRANCH STAFF TAB ---------- */}
                    {activeTab === 'staff' && (
                        <motion.div
                            key="staff-tab"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-4">

                            {/* Toolbar: Branch Filter + Staff Search */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="flex items-center gap-2.5 flex-wrap w-full sm:w-auto">
                                    <div className="relative flex-1 sm:flex-initial">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            placeholder="Search staff by name or role..."
                                            value={staffSearchTerm}
                                            onChange={e => setStaffSearchTerm(e.target.value)}
                                            className="w-full sm:w-64 pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                                        />
                                        {staffSearchTerm && (
                                            <button
                                                onClick={() => setStaffSearchTerm('')}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Branch selector */}
                                    <select
                                        value={selectedBranchFilter}
                                        onChange={e => setSelectedBranchFilter(e.target.value)}
                                        className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-teal-500/20">
                                        <option value="all">All Branches of this Business ({totalStaffCount})</option>
                                        {businessBranches.map(b => (
                                            <option key={b.id} value={b.id}>
                                                {b.name} ({b.code}) — {(branchStaffMap?.[b.id] || []).length} staff
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {businessBranches.length > 0 && (
                                    <Button
                                        size="sm"
                                        leftIcon={<UserPlus size={14} />}
                                        onClick={() => handleOpenAssignModalForBranch(
                                            selectedBranchFilter !== 'all' ? selectedBranchFilter : businessBranches[0].id
                                        )}
                                        className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                        Assign Staff to Branch
                                    </Button>
                                )}
                            </div>

                            {/* Staff Roster Content */}
                            {businessBranches.length === 0 ? (
                                <EmptyState
                                    icon={<Store size={44} className="text-teal-600" />}
                                    title="Create a branch first"
                                    description="Staff are assigned directly to branches in EtiPOS. Register a branch for this business to start deploying staff members."
                                    action={canAddBranch ? {
                                        label: 'Add Branch',
                                        onClick: () => {
                                            setEditingBranch(null);
                                            setBranchForm({ name: '', code: '', address: '', phone: '', email: '', isMainBranch: true });
                                            setBranchFormErrors({});
                                            setShowBranchForm(true);
                                        }
                                    } : undefined}
                                />
                            ) : isLoadingBranchStaff ? (
                                <div className="py-12 text-center">
                                    <Spinner size="lg" variant="teal" />
                                    <p className="text-xs text-slate-400 mt-2 font-medium">Loading branch staff rosters...</p>
                                </div>
                            ) : filteredRoster.length === 0 ? (
                                <EmptyState
                                    icon={<UsersIcon size={44} className="text-teal-600" />}
                                    title={staffSearchTerm ? 'No staff match your search' : 'No staff assigned to branches yet'}
                                    description={staffSearchTerm ? 'Try searching by a different name or role.' : 'Authorize staff members to work at branches under this business.'}
                                    action={!staffSearchTerm ? {
                                        label: 'Assign Staff to Branch',
                                        onClick: () => handleOpenAssignModalForBranch(businessBranches[0].id)
                                    } : undefined}
                                />
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredRoster.map(({ staff, branches: memberBranches }) => (
                                        <motion.div
                                            key={staff.id}
                                            whileHover={{ y: -2 }}
                                            className="bg-white rounded-2xl border border-slate-200 hover:border-teal-300 p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                                            <div>
                                                <div className="flex items-start justify-between gap-3 mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white font-bold text-base shadow-sm">
                                                            {staff.firstName?.[0]}{staff.lastName?.[0]}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h4 className="font-bold text-slate-900 text-sm truncate hover:text-teal-700 cursor-pointer" onClick={() => navigate(`/staff/${staff.id}`)}>
                                                                {staff.firstName} {staff.lastName}
                                                            </h4>
                                                            <p className="text-xs text-slate-400 truncate flex items-center gap-1 mt-0.5">
                                                                <Mail size={11} /> {staff.email}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <Badge variant={staff.isActive ? 'success' : 'danger'} size="sm">
                                                        {staff.isActive ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </div>

                                                {/* Roles */}
                                                <div className="flex flex-wrap gap-1.5 my-2">
                                                    {(staff.roles || []).map(r => (
                                                        <span
                                                            key={r.id || r.name}
                                                            className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                                            {r.name}
                                                        </span>
                                                    ))}
                                                </div>

                                                {/* Assigned Branch Badges */}
                                                <div className="mt-3 pt-2.5 border-t border-slate-100">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                                        Assigned Branch Location{memberBranches.length !== 1 ? 's' : ''}:
                                                    </p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {memberBranches.map(b => (
                                                            <span
                                                                key={b.id}
                                                                onClick={() => navigate(`/branches/${b.id}`)}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold hover:bg-teal-100 cursor-pointer transition-colors">
                                                                <Store size={12} className="text-teal-600" />
                                                                <span>{b.name}</span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                                                <button
                                                    onClick={() => navigate(`/staff/${staff.id}`)}
                                                    className="text-teal-700 font-semibold hover:underline">
                                                    View Profile →
                                                </button>
                                                <button
                                                    onClick={() => handleOpenAssignModalForBranch(memberBranches[0]?.id || businessBranches[0]?.id)}
                                                    className="text-slate-500 hover:text-teal-700 font-medium">
                                                    Modify Assignment
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* ---------- BANK ACCOUNTS TAB ---------- */}
                    {activeTab === 'accounts' && (
                        <motion.div
                            key="accounts-tab"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-4">

                            {businessAccounts.length === 0 ? (
                                <div className="p-8 sm:p-12 text-center bg-white rounded-3xl border border-slate-200/80 shadow-xs max-w-lg mx-auto space-y-4">
                                    <div className="w-16 h-16 rounded-2xl bg-teal-50 border border-teal-100 text-teal-600 flex items-center justify-center mx-auto">
                                        <CreditCard size={32} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900">No bank accounts assigned to this business</h3>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Assign an existing bank account from your organization or create a new dedicated account for this business entity.
                                        </p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                                        <Button
                                            variant="secondary"
                                            leftIcon={<Link2 size={14} />}
                                            onClick={() => {
                                                setAssignAccountModalSearch('');
                                                setShowAssignAccountModal(true);
                                            }}
                                            className="w-full sm:w-auto border-teal-200 text-teal-800 hover:bg-teal-50 font-semibold">
                                            Assign Existing Account
                                        </Button>
                                        <Button
                                            leftIcon={<Plus size={14} />}
                                            onClick={() => {
                                                setEditingAccount(null);
                                                setAccountForm({
                                                    bankName: '', accountNumber: '', accountName: '', accountType: 'current',
                                                    currency: 'NGN', openingBalance: '0', branchId: '', isDefault: false
                                                });
                                                setShowAccountForm(true);
                                            }}
                                            className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                            Add Account
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {businessAccounts.map(account => {
                                        const cfg = accountTypeConfig[account.accountType] || accountTypeConfig.other;
                                        const Icon = cfg.icon;
                                        const linkedBranch = businessBranches.find(b => b.id === account.branchId);

                                        return (
                                            <motion.div
                                                key={account.id}
                                                whileHover={{ y: -3 }}
                                                className={clsx(
                                                    'p-6 rounded-3xl border-2 transition-all shadow-sm flex flex-col justify-between',
                                                    cfg.bgLight,
                                                    cfg.border
                                                )}>
                                                <div>
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className={`p-3 rounded-2xl bg-gradient-to-br ${cfg.gradient} text-white shadow-md`}>
                                                            <Icon size={20} />
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1">
                                                            {account.isDefault && <Badge variant="success" size="sm">Default</Badge>}
                                                            <Badge variant={account.isActive ? 'success' : 'danger'} size="sm">
                                                                {account.isActive ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </div>
                                                    </div>

                                                    <h4 className="font-extrabold text-slate-900 text-lg">{account.bankName}</h4>
                                                    <p className="text-xs text-slate-500 font-mono tracking-wider mt-0.5">{account.accountNumber}</p>
                                                    <p className="text-xs text-slate-400 mt-0.5">{account.accountName}</p>

                                                    {linkedBranch ? (
                                                        <p className="text-xs text-teal-700 font-medium mt-2 flex items-center gap-1">
                                                            <Store size={11} /> Linked to: {linkedBranch.name}
                                                        </p>
                                                    ) : (
                                                        <p className="text-xs text-slate-400 font-medium mt-2">
                                                            Entity-level vault (all branches)
                                                        </p>
                                                    )}

                                                    <div className="mt-4 pt-3 border-t border-slate-200/60">
                                                        <p className="text-[10px] font-bold uppercase text-slate-400">Current Balance</p>
                                                        <p className="text-2xl font-black text-emerald-600 mt-0.5">
                                                            {formatNaira(account.currentBalance)}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between gap-2 mt-5 pt-3 border-t border-slate-200/60">
                                                    <Badge variant="neutral" size="sm">{cfg.label}</Badge>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => assignAccountMutation.mutate({ accountId: account.id, targetBusinessId: null, targetBranchId: null })}
                                                            className="text-xs font-semibold text-amber-600 hover:text-amber-800 hover:underline flex items-center gap-1"
                                                            title="Unlink from this business">
                                                            <Unlink size={11} /> Unlink
                                                        </button>
                                                        <span className="text-slate-300">•</span>
                                                        <button
                                                            onClick={() => {
                                                                setEditingAccount(account);
                                                                setAccountForm({
                                                                    bankName: account.bankName,
                                                                    accountNumber: account.accountNumber,
                                                                    accountName: account.accountName,
                                                                    accountType: account.accountType,
                                                                    currency: account.currency,
                                                                    openingBalance: account.openingBalance,
                                                                    branchId: account.branchId || '',
                                                                    isDefault: account.isDefault
                                                                });
                                                                setShowAccountForm(true);
                                                            }}
                                                            className="text-xs font-semibold text-teal-700 hover:text-teal-900 hover:underline">
                                                            Edit
                                                        </button>
                                                        <span className="text-slate-300">•</span>
                                                        <button
                                                            onClick={() => setDeleteTarget({ type: 'account', id: account.id, name: account.bankName })}
                                                            className="text-xs font-semibold text-rose-500 hover:text-rose-700 hover:underline">
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* ---------- CURRENCIES TAB ---------- */}
                    {activeTab === 'currencies' && (
                        <motion.div
                            key="currencies-tab"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-4">

                            {(currencies || []).length === 0 ? (
                                <EmptyState
                                    icon={<Coins size={44} className="text-teal-600" />}
                                    title="No currencies configured"
                                    description="Manage allowed settlement currencies and foreign exchange rates in Settings."
                                    action={{ label: 'Go to Currency Settings', onClick: () => navigate('/settings') }}
                                />
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {(currencies || []).map(currency => (
                                        <div
                                            key={currency.id}
                                            className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-2xl font-black text-slate-800">{currency.symbol}</span>
                                                    <span className="text-sm font-bold text-slate-900">{currency.code}</span>
                                                    {currency.isBaseCurrency && <Badge variant="success" size="sm">Base</Badge>}
                                                </div>
                                                <p className="text-xs text-slate-500 mt-1">{currency.name}</p>
                                                <p className="text-[11px] text-slate-400 mt-0.5">
                                                    Exchange Rate: {currency.exchangeRate} · Decimals: {currency.decimalPlaces || 2}
                                                </p>
                                            </div>
                                            <div className="p-3 rounded-2xl bg-slate-50 text-slate-400">
                                                <Coins size={20} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ========== MODALS ========== */}

                {/* Edit Business Profile Modal */}
                <Modal open={showEditBusiness} onClose={() => setShowEditBusiness(false)} title="Edit Business Profile" icon={Building2}>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Business Name *"
                                value={editForm.name}
                                error={editFormErrors.name}
                                onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Business Name"
                                required
                            />
                            <Input
                                label="Short Code *"
                                value={editForm.shortCode}
                                error={editFormErrors.shortCode}
                                onChange={e => setEditForm(prev => ({ ...prev, shortCode: e.target.value.toUpperCase() }))}
                                placeholder="ETI"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                Business Sector / Type
                            </label>
                            <select
                                value={editForm.type}
                                onChange={e => setEditForm(prev => ({ ...prev, type: e.target.value }))}
                                className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                                {Object.entries(businessTypes).map(([key, item]) => (
                                    <option key={key} value={key}>
                                        {item.emoji} {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <Input
                            label="Business Description"
                            value={editForm.description}
                            error={editFormErrors.description}
                            onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Brief description of operations"
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Contact Email"
                                type="email"
                                value={editForm.contactEmail}
                                error={editFormErrors.contactEmail}
                                onChange={e => setEditForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                                placeholder="info@company.com"
                            />
                            <Input
                                label="Contact Phone"
                                value={editForm.contactPhone}
                                error={editFormErrors.contactPhone}
                                onChange={e => setEditForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                                placeholder="+234..."
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Website"
                                value={editForm.website}
                                error={editFormErrors.website}
                                onChange={e => setEditForm(prev => ({ ...prev, website: e.target.value }))}
                                placeholder="https://company.com"
                            />
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Brand Accent Color
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={editForm.brandColor}
                                        onChange={e => setEditForm(prev => ({ ...prev, brandColor: e.target.value }))}
                                        className="w-10 h-10 rounded-xl border-2 border-slate-200 cursor-pointer p-0.5"
                                    />
                                    <input
                                        type="text"
                                        value={editForm.brandColor}
                                        onChange={e => setEditForm(prev => ({ ...prev, brandColor: e.target.value }))}
                                        className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-xl text-xs font-mono"
                                    />
                                </div>
                            </div>
                        </div>

                        <Input
                            label="Physical Address"
                            value={editForm.address}
                            error={editFormErrors.address}
                            onChange={e => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="Headquarters address"
                        />
                    </div>

                    <div className="flex justify-end gap-3 mt-7">
                        <Button variant="secondary" onClick={() => setShowEditBusiness(false)}>Cancel</Button>
                        <Button
                            onClick={() => updateBusinessMutation.mutate(editForm)}
                            isLoading={updateBusinessMutation.isPending}
                            disabled={!editForm.name || !editForm.shortCode}
                            leftIcon={<Save size={14} />}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            Save Changes
                        </Button>
                    </div>
                </Modal>

                {/* Add/Edit Branch Modal */}
                <Modal
                    open={showBranchForm}
                    onClose={() => setShowBranchForm(false)}
                    title={editingBranch ? 'Edit Branch' : `Add Branch to ${business.name}`}
                    icon={Store}>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Branch Name *"
                                value={branchForm.name}
                                error={branchFormErrors.name}
                                onChange={e => setBranchForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Main Branch"
                                required
                            />
                            <Input
                                label="Branch Code *"
                                value={branchForm.code}
                                error={branchFormErrors.code}
                                onChange={e => setBranchForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                                placeholder="MB-01"
                                required
                            />
                        </div>

                        <Input
                            label="Address"
                            value={branchForm.address}
                            error={branchFormErrors.address}
                            onChange={e => setBranchForm(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="Street address, City"
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Phone Number"
                                value={branchForm.phone}
                                error={branchFormErrors.phone}
                                onChange={e => setBranchForm(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="+234..."
                            />
                            <Input
                                label="Email Address"
                                value={branchForm.email}
                                error={branchFormErrors.email}
                                onChange={e => setBranchForm(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="branch@domain.com"
                            />
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer p-3 bg-amber-50 rounded-2xl border border-amber-200">
                            <input
                                type="checkbox"
                                checked={branchForm.isMainBranch}
                                onChange={e => setBranchForm(prev => ({ ...prev, isMainBranch: e.target.checked }))}
                                className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                            />
                            <div>
                                <span className="text-sm font-bold text-amber-900">Mark as Primary Main Branch</span>
                                <p className="text-xs text-amber-700">Designated headquarters for this business.</p>
                            </div>
                        </label>
                    </div>

                    <div className="flex justify-end gap-3 mt-7">
                        <Button variant="secondary" onClick={() => setShowBranchForm(false)}>Cancel</Button>
                        <Button
                            onClick={() => saveBranchMutation.mutate()}
                            isLoading={saveBranchMutation.isPending}
                            disabled={!branchForm.name || !branchForm.code}
                            leftIcon={<Save size={14} />}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            {editingBranch ? 'Save Changes' : 'Create Branch'}
                        </Button>
                    </div>
                </Modal>

                {/* Bank Account Modal */}
                <Modal
                    open={showAccountForm}
                    onClose={() => setShowAccountForm(false)}
                    title={editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}
                    icon={CreditCard}>
                    <div className="space-y-4">
                        <Input
                            label="Bank Name *"
                            value={accountForm.bankName}
                            onChange={e => setAccountForm(prev => ({ ...prev, bankName: e.target.value }))}
                            placeholder="e.g., Access Bank, GTBank"
                            required
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Account Number *"
                                value={accountForm.accountNumber}
                                onChange={e => setAccountForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                                placeholder="0123456789"
                                required
                            />
                            <Input
                                label="Account Name *"
                                value={accountForm.accountName}
                                onChange={e => setAccountForm(prev => ({ ...prev, accountName: e.target.value }))}
                                placeholder="Business Operating Acct"
                                required
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Account Type
                                </label>
                                <select
                                    value={accountForm.accountType}
                                    onChange={e => setAccountForm(prev => ({ ...prev, accountType: e.target.value }))}
                                    className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                                    <option value="current">Current</option>
                                    <option value="savings">Savings</option>
                                    <option value="cash">Cash Vault</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <Input
                                label="Opening Balance (₦)"
                                type="number"
                                value={accountForm.openingBalance}
                                onChange={e => setAccountForm(prev => ({ ...prev, openingBalance: e.target.value }))}
                                placeholder="0.00"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                Linked Branch (Optional)
                            </label>
                            <select
                                value={accountForm.branchId}
                                onChange={e => setAccountForm(prev => ({ ...prev, branchId: e.target.value }))}
                                className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                                <option value="">Business-level (shared across branches)</option>
                                {businessBranches.map(b => (
                                    <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
                                ))}
                            </select>
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer p-3 bg-blue-50 rounded-2xl border border-blue-200">
                            <input
                                type="checkbox"
                                checked={accountForm.isDefault}
                                onChange={e => setAccountForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                                className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                                <span className="text-sm font-bold text-blue-900">Set as default settlement account</span>
                                <p className="text-xs text-blue-600">Primary account for transaction settlements.</p>
                            </div>
                        </label>
                    </div>

                    <div className="flex justify-end gap-3 mt-7">
                        <Button variant="secondary" onClick={() => setShowAccountForm(false)}>Cancel</Button>
                        <Button
                            onClick={() => saveAccountMutation.mutate()}
                            isLoading={saveAccountMutation.isPending}
                            disabled={!accountForm.bankName || !accountForm.accountNumber}
                            leftIcon={<Save size={14} />}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            {editingAccount ? 'Save Changes' : 'Create Account'}
                        </Button>
                    </div>
                </Modal>

                {/* Assign Staff to Branch Modal */}
                <AnimatePresence>
                    {showStaffAssignModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setShowStaffAssignModal(false)}>
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-100">
                                <div className="px-7 py-5 bg-gradient-to-r from-teal-700 via-emerald-600 to-teal-800 text-white flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white/20">
                                            <UsersIcon size={20} className="text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-white">Assign Staff to Branch</h2>
                                            <p className="text-teal-100 text-xs">{business.name}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowStaffAssignModal(false)}
                                        className="p-2 rounded-xl bg-white/15 text-white hover:bg-white/30 transition-colors">
                                        <X size={18} />
                                    </button>
                                </div>

                                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                                    {/* Destination Branch Picker */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            Destination Branch Location *
                                        </label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {businessBranches.map(b => {
                                                const isSelected = assignTargetBranchId === b.id;
                                                return (
                                                    <button
                                                        key={b.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setAssignTargetBranchId(b.id);
                                                            const currentStaffInB = (branchStaffMap?.[b.id] || []).map(s => s.id);
                                                            setModalSelectedStaffIds(currentStaffInB);
                                                        }}
                                                        className={clsx(
                                                            'flex items-center gap-2.5 p-3 rounded-2xl border-2 text-left transition-all',
                                                            isSelected
                                                                ? 'border-teal-600 bg-teal-50/70 shadow-xs'
                                                                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                        )}>
                                                        <Store size={16} className={isSelected ? 'text-teal-700' : 'text-slate-400'} />
                                                        <div className="min-w-0">
                                                            <p className={clsx('text-xs font-bold truncate', isSelected ? 'text-teal-900' : 'text-slate-800')}>
                                                                {b.name}
                                                            </p>
                                                            <p className="text-[10px] text-slate-400 font-mono">{b.code}</p>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="pt-2 border-t border-slate-100">
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <p className="text-xs text-slate-500 font-medium">
                                                Select staff members to deploy at this branch ({modalSelectedStaffIds.length} selected)
                                            </p>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setModalSelectedStaffIds((allTenantStaff || []).map(s => s.id))}
                                                    className="text-[11px] font-semibold text-teal-700 hover:underline">
                                                    Select All
                                                </button>
                                                <span className="text-slate-300">•</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setModalSelectedStaffIds([])}
                                                    className="text-[11px] font-semibold text-slate-500 hover:underline">
                                                    Clear
                                                </button>
                                            </div>
                                        </div>

                                        {/* Search input */}
                                        <div className="relative mb-3">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                            <input
                                                type="text"
                                                placeholder="Search staff by name or role..."
                                                value={assignModalSearch}
                                                onChange={e => setAssignModalSearch(e.target.value)}
                                                className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                                            />
                                        </div>

                                        {/* Staff List */}
                                        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                                            {isLoadingAllStaff ? (
                                                <div className="py-8 text-center"><Spinner size="md" /></div>
                                            ) : (allTenantStaff || []).filter(s => {
                                                if (!assignModalSearch) return true;
                                                const q = assignModalSearch.toLowerCase();
                                                return `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
                                                    s.email.toLowerCase().includes(q) ||
                                                    s.roles?.some(r => r.name.toLowerCase().includes(q));
                                            }).map(staff => {
                                                const isSelected = modalSelectedStaffIds.includes(staff.id);
                                                return (
                                                    <label
                                                        key={staff.id}
                                                        className={clsx(
                                                            'flex items-center gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-all',
                                                            isSelected
                                                                ? 'border-teal-500 bg-teal-50/70 shadow-xs'
                                                                : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50/60'
                                                        )}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleModalStaffSelection(staff.id)}
                                                            className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                                        />
                                                        <div className="w-8 h-8 rounded-xl bg-teal-100 text-teal-800 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                                            {staff.firstName?.[0]}{staff.lastName?.[0]}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-xs font-bold text-slate-800 truncate">
                                                                    {staff.firstName} {staff.lastName}
                                                                </p>
                                                                {staff.roles?.map(r => (
                                                                    <span key={r.id || r.name} className="px-1.5 py-0.2 rounded text-[9px] font-medium bg-slate-200 text-slate-700">
                                                                        {r.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                            <p className="text-[11px] text-slate-400 truncate">{staff.email}</p>
                                                        </div>
                                                        <Badge variant={staff.isActive ? 'success' : 'danger'} size="sm">
                                                            {staff.isActive ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="px-7 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                                    <span className="text-xs text-slate-500 font-medium">{modalSelectedStaffIds.length} staff selected</span>
                                    <div className="flex gap-2">
                                        <Button variant="secondary" size="sm" onClick={() => setShowStaffAssignModal(false)}>
                                            Cancel
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => assignStaffMutation.mutate()}
                                            isLoading={assignStaffMutation.isPending}
                                            disabled={!assignTargetBranchId}
                                            leftIcon={<Save size={14} />}
                                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                            Save Assignments
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                    {/* Assign Existing Account Modal */}
                    {showAssignAccountModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
                            <motion.div
                                initial={{ scale: 0.95, y: 15 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 15 }}
                                className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200">
                                {/* Header */}
                                <div className="px-6 py-5 bg-gradient-to-r from-teal-800 to-slate-900 text-white flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-bold flex items-center gap-2">
                                            <Link2 size={20} className="text-teal-300" />
                                            Assign Bank Accounts to {business?.name}
                                        </h3>
                                        <p className="text-xs text-teal-100/80 mt-0.5">
                                            Link existing settlement accounts or petty cash vaults to this business entity.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowAssignAccountModal(false)}
                                        className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition">
                                        <X size={18} />
                                    </button>
                                </div>

                                {/* Content & Search */}
                                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                                    <div className="relative">
                                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                        <Input
                                            placeholder="Search by bank name, account number or account name..."
                                            value={assignAccountModalSearch}
                                            onChange={(e) => setAssignAccountModalSearch(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        {filteredAssignAccounts.length === 0 ? (
                                            <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                                <CreditCard className="mx-auto text-slate-400 mb-2" size={32} />
                                                <p className="text-sm font-bold text-slate-700">No bank accounts found</p>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {allTenantAccounts.length === 0
                                                        ? "You don't have any created bank accounts yet. Click below to create one."
                                                        : "No accounts match your search filter."}
                                                </p>
                                            </div>
                                        ) : (
                                            filteredAssignAccounts.map((acc) => {
                                                const isAssignedToThisBusiness = acc.businessId === businessId;
                                                const linkedBranch = businessBranches.find(b => b.id === acc.branchId);
                                                const cfg = accountTypeConfig[acc.accountType] || accountTypeConfig.other;
                                                const Icon = cfg.icon;

                                                return (
                                                    <div
                                                        key={acc.id}
                                                        className={clsx(
                                                            "p-4 rounded-2xl border-2 transition-all flex items-center justify-between gap-4",
                                                            isAssignedToThisBusiness ? "border-teal-500/80 bg-teal-50/50" : "border-slate-200 hover:border-slate-300 bg-white"
                                                        )}>
                                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                                            <div className={`p-2.5 rounded-xl bg-gradient-to-br ${cfg.gradient} text-white shadow-sm flex-shrink-0`}>
                                                                <Icon size={18} />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <h4 className="font-bold text-slate-900 text-sm">{acc.bankName}</h4>
                                                                    <Badge variant="neutral" size="sm">{cfg.label}</Badge>
                                                                    {isAssignedToThisBusiness && (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-800">
                                                                            <Check size={10} /> Assigned to this Business
                                                                        </span>
                                                                    )}
                                                                    {linkedBranch && (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                                                                            <Store size={10} /> Linked to: {linkedBranch.name}
                                                                        </span>
                                                                    )}
                                                                    {!acc.businessId && (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
                                                                            Unassigned Vault
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                                                    <span className="font-mono">{acc.accountNumber}</span>
                                                                    <span>•</span>
                                                                    <span className="truncate">{acc.accountName}</span>
                                                                    <span>•</span>
                                                                    <span className="font-bold text-emerald-600">{formatNaira(acc.currentBalance)}</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Action button */}
                                                        <div className="flex-shrink-0">
                                                            {isAssignedToThisBusiness ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="secondary"
                                                                    onClick={() => assignAccountMutation.mutate({ accountId: acc.id, targetBusinessId: null, targetBranchId: null })}
                                                                    isLoading={assignAccountMutation.isPending}
                                                                    className="border-amber-200 text-amber-700 hover:bg-amber-50 text-xs">
                                                                    <Unlink size={12} className="mr-1" /> Unlink
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => assignAccountMutation.mutate({ accountId: acc.id, targetBusinessId: businessId! })}
                                                                    isLoading={assignAccountMutation.isPending}
                                                                    className="bg-teal-600 hover:bg-teal-700 text-white text-xs">
                                                                    <Link2 size={12} className="mr-1" /> Assign to Business
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAssignAccountModal(false);
                                            setEditingAccount(null);
                                            setAccountForm({
                                                bankName: '', accountNumber: '', accountName: '', accountType: 'current',
                                                currency: 'NGN', openingBalance: '0', branchId: '', isDefault: false
                                            });
                                            setShowAccountForm(true);
                                        }}
                                        className="text-xs font-bold text-teal-700 hover:text-teal-900 hover:underline flex items-center gap-1">
                                        <Plus size={14} /> Need a different bank? Create New Bank Account
                                    </button>
                                    <Button variant="secondary" size="sm" onClick={() => setShowAssignAccountModal(false)}>
                                        Done
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Delete Confirmation Modal */}
                <AnimatePresence>
                    {deleteTarget && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setDeleteTarget(null)}>
                            <motion.div
                                initial={{ scale: 0.95 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-7 text-center border border-slate-100">
                                <div className="w-16 h-16 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
                                    <Trash2 size={28} className="text-rose-500" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900">Delete {deleteTarget.type}?</h3>
                                <p className="text-sm text-slate-500 mt-2">
                                    This will permanently remove <strong>{deleteTarget.name}</strong>. This action cannot be reversed.
                                </p>
                                <div className="flex gap-3 mt-7">
                                    <Button variant="secondary" onClick={() => setDeleteTarget(null)} className="flex-1">Cancel</Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => deleteMutation.mutate({ type: deleteTarget.type, id: deleteTarget.id })}
                                        isLoading={deleteMutation.isPending}
                                        className="flex-1">
                                        Delete
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

            </div>
        </div>
    );
}